import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  User,
  Building2,
  ArrowLeft,
  Save,
  UploadCloud,
  Trash2,
  Loader2,
  Lock,
} from "lucide-react";
import { toast } from "react-toastify";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import FormControl from "../../components/common/FormControl";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import methodModel from "../../methods/methods";
import { login_success } from "../../actions/user";

export default function EditProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxUser = useSelector((state) => state.user);
  const fileInputRef = useRef(null);

  const isTenantAdmin =
    reduxUser?.isTenantAdmin ||
    reduxUser?.role?.name === "Tenant-Admin" ||
    reduxUser?.roleType === "tenant_admin" ||
    Boolean(reduxUser?.company_name || reduxUser?.subdomain || reduxUser?.tenantId);

  const [form, setForm] = useState({
    firstName: reduxUser?.firstName || "",
    lastName: reduxUser?.lastName || "",
    email: reduxUser?.email || "",
    mobileNo: reduxUser?.adminPhone || reduxUser?.mobileNo || "",
    company_name: reduxUser?.company_name || reduxUser?.companyName || "",
    portal_title: reduxUser?.portal_title || "",
    support_email: reduxUser?.support_email || reduxUser?.contact_email || "",
    support_phone: reduxUser?.support_phone || reduxUser?.contact_phone || "",
    primary_color: reduxUser?.primary_color || "#0F172A",
    secondary_color: reduxUser?.secondary_color || "#2563EB",
    accent_color: reduxUser?.accent_color || "#F59E0B",
    street:
      typeof reduxUser?.address === "object"
        ? reduxUser.address?.street || ""
        : reduxUser?.address || "",
    city:
      typeof reduxUser?.address === "object"
        ? reduxUser.address?.city || ""
        : reduxUser?.city || "",
    state:
      typeof reduxUser?.address === "object"
        ? reduxUser.address?.state || ""
        : reduxUser?.state || "",
    zipCode:
      typeof reduxUser?.address === "object"
        ? reduxUser.address?.zipCode || ""
        : reduxUser?.pinCode || "",
    country: "USA",
    image: reduxUser?.logo || reduxUser?.logo_url || reduxUser?.image || "",
    id: reduxUser?.id || reduxUser?._id,
  });

  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch fresh organization profile and admin personal profile on mount
  useEffect(() => {
    const fetchPortalDetails = async () => {
      try {
        // 1. Fetch organization details (brand colors, contacts, title, address)
        const pRes = await ApiClient.get("tenants/portal/profile", {}, "", true);
        if (pRes?.success && (pRes?.data || pRes?.tenant || pRes?.profile)) {
          const pData = pRes.data || pRes.tenant || pRes.profile;
          setForm((prev) => ({
            ...prev,
            company_name: pData.company_name || prev.company_name,
            portal_title: pData.portal_title || prev.portal_title,
            support_email: pData.support_email || pData.contact_email || prev.support_email,
            support_phone: pData.support_phone || pData.contact_phone || prev.support_phone,
            primary_color: pData.primary_color || prev.primary_color || "#0F172A",
            secondary_color: pData.secondary_color || prev.secondary_color || "#2563EB",
            accent_color: pData.accent_color || prev.accent_color || "#F59E0B",
            street: pData.address?.street || prev.street,
            city: pData.address?.city || prev.city,
            state: pData.address?.state || prev.state,
            zipCode: pData.address?.zipCode || prev.zipCode,
            country: pData.address?.country || prev.country || "USA",
            image: pData.logo || pData.logo_url || prev.image,
          }));
        }
      } catch (err) {}

      try {
        // 2. Fetch admin user profile details
        const uRes = await ApiClient.get("user/admin-profile", {}, "", true);
        if (uRes?.success && (uRes?.data || uRes?.user_data)) {
          const uData = uRes.data || uRes.user_data;
          setForm((prev) => ({
            ...prev,
            firstName: uData.firstName || prev.firstName,
            lastName: uData.lastName || prev.lastName,
            email: uData.email || prev.email,
            mobileNo: uData.mobileNo || uData.adminPhone || prev.mobileNo,
          }));
        }
      } catch (err) {}
    };

    fetchPortalDetails();
  }, []);

  const handleChange = (field, val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG, SVG).");
      return;
    }

    setLogoUploading(true);
    loader(true);

    try {
      let res;
      if (isTenantAdmin) {
        try {
          res = await ApiClient.postFormData("tenants/portal/logo", { file });
        } catch (err) {
          res = await ApiClient.postFormData("upload/logo", { file });
        }
      } else {
        res = await ApiClient.postFormData("upload/image", { file });
      }

      if (res?.success || res?.fileUrl || res?.location || res?.fileName) {
        let uploadedUrl =
          res?.data?.fileUrl ||
          res?.fileUrl ||
          res?.data?.filePath ||
          res?.filePath ||
          res?.data?.url ||
          res?.location ||
          res?.fileName;

        if (uploadedUrl && typeof uploadedUrl === "string") {
          if (window.location.protocol === "https:" && uploadedUrl.startsWith("http://")) {
            uploadedUrl = uploadedUrl.replace("http://", "https://");
          }
          setForm((prev) => ({ ...prev, image: uploadedUrl }));
          toast.success("Logo uploaded successfully.");
        }
      } else {
        toast.error(res?.message || "Failed to upload image.");
      }
    } catch (err) {
      toast.error("Failed to upload image.");
    } finally {
      setLogoUploading(false);
      loader(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.firstName.trim()) {
      setErrors((prev) => ({ ...prev, firstName: "First name is required." }));
      return;
    }

    setLoading(true);
    loader(true);

    const organizationPayload = {
      company_name: form.company_name.trim(),
      portal_title: form.portal_title.trim(),
      support_email: form.support_email.trim(),
      support_phone: form.support_phone,
      primary_color: form.primary_color || "#0F172A",
      secondary_color: form.secondary_color || "#2563EB",
      accent_color: form.accent_color || "#F59E0B",
      address: {
        street: form.street.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zipCode: form.zipCode.trim(),
        country: form.country || "USA",
      },
    };

    const userPayload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      fullName: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
      mobileNo: (form.mobileNo || "").replace(/\D/g, ""),
      adminPhone: form.mobileNo,
      company_name: form.company_name.trim(),
      companyName: form.company_name.trim(),
      portal_title: form.portal_title.trim(),
      support_email: form.support_email.trim(),
      support_phone: form.support_phone,
      address: organizationPayload.address,
      image: form.image,
      logo: form.image,
      logo_url: form.image,
      primary_color: form.primary_color,
      secondary_color: form.secondary_color,
      accent_color: form.accent_color,
    };

    try {
      // 1. If tenant admin, update tenant portal profile
      if (isTenantAdmin) {
        try {
          await ApiClient.put("tenants/portal/profile", organizationPayload);
        } catch (pErr) {
          console.warn("tenants/portal/profile PUT error:", pErr);
        }
      }

      // 2. Update user admin personal details
      const res = await ApiClient.put("user/profile", userPayload);

      loader(false);
      setLoading(false);

      if (res?.success || res?.status === 200 || !res?.error) {
        toast.success(res?.message || "Profile updated successfully!");
        const updated = {
          ...reduxUser,
          ...userPayload,
          ...organizationPayload,
          _id: reduxUser?._id || reduxUser?.id,
          id: reduxUser?._id || reduxUser?.id,
        };
        dispatch(login_success(updated));
        localStorage.setItem("logedInUser", JSON.stringify(updated));
        navigate("/profile");
      } else {
        toast.error(res?.message || "Failed to update profile.");
      }
    } catch (err) {
      loader(false);
      setLoading(false);
      toast.error(err?.response?.data?.message || "Failed to update profile.");
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[75px] lg:mt-[95px] bg-[#F4F7FB] py-8 lg:py-12">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          {/* Top Actions Bar */}
          <div className="flex items-center justify-between mb-8 bg-white p-5 rounded-2xl border border-slate-300 shadow-sm">
            <div className="flex items-center gap-3">
              <Link
                to="/profile"
                className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 transition-colors"
                title="Back to Profile"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Edit Profile
                </h1>
                <p className="text-xs sm:text-sm text-slate-700 font-medium mt-0.5">
                  Update your contact details and business information.
                </p>
              </div>
            </div>

            <Link
              to="/changepassword"
              className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold transition-all shadow-xs cursor-pointer"
            >
              <Lock className="w-3.5 h-3.5 text-[#115B9D]" />
              <span>Change Password</span>
            </Link>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* Avatar / Logo Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-300 p-6">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-900 mb-3">
                {isTenantAdmin ? "Organization Logo / Avatar" : "Profile Picture"}
              </label>

              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20 rounded-2xl border-2 border-slate-200 p-1 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                  {form.image ? (
                    <img
                      src={methodModel.userImg(form.image)}
                      alt="Profile Preview"
                      className="w-full h-full object-contain rounded-xl"
                    />
                  ) : (
                    <div className="w-full h-full rounded-xl bg-slate-200 flex items-center justify-center text-slate-500 text-xl font-bold">
                      {form.firstName ? form.firstName.charAt(0).toUpperCase() : "U"}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={logoUploading}
                      className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-xl bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold transition-all shadow-xs cursor-pointer"
                    >
                      {logoUploading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#115B9D]" />
                      ) : (
                        <UploadCloud className="w-3.5 h-3.5 text-[#115B9D]" />
                      )}
                      <span>{form.image ? "Change Image" : "Upload Image"}</span>
                    </button>

                    {form.image && (
                      <button
                        type="button"
                        onClick={() => handleChange("image", "")}
                        className="inline-flex items-center gap-1 px-2.5 h-9 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200 text-xs font-bold transition-all cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Remove</span>
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500">
                    PNG, JPG, or SVG with transparent background (max 5MB).
                  </p>
                </div>
              </div>
            </div>

            {/* Personal Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                <User className="w-4 h-4 text-[#115B9D]" />
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
                  Personal Information
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormControl
                  type="text"
                  name="firstName"
                  label="First Name"
                  required={true}
                  placeholder="Enter first name"
                  value={form.firstName}
                  error={errors.firstName}
                  onChange={(val) => handleChange("firstName", val)}
                />
                <FormControl
                  type="text"
                  name="lastName"
                  label="Last Name"
                  placeholder="Enter last name"
                  value={form.lastName}
                  onChange={(val) => handleChange("lastName", val)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormControl
                  type="email"
                  name="email"
                  label="Login Email (Cannot be changed)"
                  disabled={true}
                  value={form.email}
                />
                <FormControl
                  type="phone"
                  name="mobileNo"
                  label="Mobile / Phone Number"
                  placeholder="+1 (800) 555-0199"
                  value={form.mobileNo}
                  onChange={(val) => handleChange("mobileNo", val)}
                />
              </div>
            </div>

            {/* Organization Info Card (If Tenant Admin) */}
            {isTenantAdmin && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                  <Building2 className="w-4 h-4 text-[#115B9D]" />
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
                    Organization Information
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormControl
                    type="text"
                    name="company_name"
                    label="Company Name"
                    placeholder="Enter company name"
                    value={form.company_name}
                    onChange={(val) => handleChange("company_name", val)}
                  />
                  <FormControl
                    type="text"
                    name="portal_title"
                    label="Portal Title"
                    placeholder="Enter portal title"
                    value={form.portal_title}
                    onChange={(val) => handleChange("portal_title", val)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormControl
                    type="email"
                    name="support_email"
                    label="Support Email"
                    placeholder="support@company.com"
                    value={form.support_email}
                    onChange={(val) => handleChange("support_email", val)}
                  />
                  <FormControl
                    type="phone"
                    name="support_phone"
                    label="Support Phone"
                    placeholder="+1 (800) 555-0100"
                    value={form.support_phone}
                    onChange={(val) => handleChange("support_phone", val)}
                  />
                </div>
              </div>
            )}

            {/* Address Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-300 p-6 space-y-4">
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide border-b border-slate-200 pb-3">
                Address Details
              </h2>

              <FormControl
                type="text"
                name="street"
                label="Street Address"
                placeholder="100 Industrial Parkway, Suite 400"
                value={form.street}
                onChange={(val) => handleChange("street", val)}
              />

              <div className="grid grid-cols-3 gap-3">
                <FormControl
                  type="text"
                  name="city"
                  label="City"
                  placeholder="Dallas"
                  value={form.city}
                  onChange={(val) => handleChange("city", val)}
                />
                <FormControl
                  type="text"
                  name="state"
                  label="State"
                  placeholder="TX"
                  value={form.state}
                  onChange={(val) => handleChange("state", val)}
                />
                <FormControl
                  type="text"
                  name="zipCode"
                  label="Zip Code"
                  placeholder="75001"
                  value={form.zipCode}
                  onChange={(val) => handleChange("zipCode", val)}
                />
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Link
                to="/profile"
                className="px-5 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 text-xs font-bold transition-all flex items-center justify-center shadow-xs"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-xl bg-gradient-to-r from-[#115B9D] to-[#0E528F] hover:from-[#0E528F] hover:to-[#08335C] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer disabled:opacity-75"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>{loading ? "Saving Changes..." : "Save Changes"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}
